import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { CreateDoByBBPRequest} from 'src/app/core/models/transport-operation/create-do-request';
import { LaasOption } from 'src/app/core/models/laas-option';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { DeliveryOrderService } from '../../services/delivery-order.service';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { PoServiceService } from '../../services/po-service.service';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { startOfDay } from 'date-fns';
import { PlantService } from '../../services/plant-service';
import { GlobalConstants } from 'src/app/core/constants/global-constants.constant';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';

type CreateDoColumnField =
  '__delete' | 'sku' | 'originalQty' | 'inspectionLot' | 'assignedVendorLot' | 'assignedVendorDC' |
  'assignedCoo' | 'assignedBrand' | 'bbpLine' | 'bbpFace' | 'bbpMachine' | 'bbpZone' | 'bbpLot' | 'errorReason';

interface CreateDoRuleContext {
  calloffReason: string | null;
}

interface CreateDoColumnSchema {
  field: CreateDoColumnField;
  header: string;
  sort: boolean;
  type: 'action' | 'text' | 'number' | 'dropdown' | 'bbp' | 'readonly';
  required?: boolean;
  optionsSource?: 'countryOptions' | 'brandOptions';
  showFieldAConstraint?: boolean;
  rules?: {
    editable?: (context: CreateDoRuleContext) => boolean;
    visible?: (context: CreateDoRuleContext) => boolean;
    sanitizeWhenHidden?: boolean;
  };
}

interface CellViewModel {
  editable: boolean;
  visible: boolean;
  value: any;
}

@Component({
  selector: 'laas-create-do',
  templateUrl: './create-do.component.html',
  styleUrls: ['./create-do.component.scss']
})
export class CreateDoComponent extends SessionBaseComponent implements OnInit {
  private readonly MO_USE_REASON = 'MO_USE';
  private readonly IQC_XRAY_REASON = 'IQCXray';
  private readonly columnSchemas: ReadonlyArray<CreateDoColumnSchema> = [
    { field: '__delete', header: 'CreateMoItem.CreateForm.Delete', sort: false, type: 'action' },
    { field: 'sku', header: 'CreateMoItem.CreateForm.Sku', sort: false, type: 'text', required: true },
    { field: 'originalQty', header: 'CreateMoItem.CreateForm.Qty', sort: false, type: 'number', required: true,
      rules: { editable: (context) => context.calloffReason !== this.IQC_XRAY_REASON } },
    { field: 'inspectionLot', header: 'CreateMoItem.CreateForm.InspectionLot', sort: false, type: 'text' },
    { field: 'assignedVendorLot', header: 'CreateMoItem.CreateForm.AssignedVendorLot', sort: false, type: 'text', showFieldAConstraint: true },
    { field: 'assignedVendorDC', header: 'CreateMoItem.CreateForm.AssignedVendorDC', sort: false, type: 'text', showFieldAConstraint: true },
    { field: 'assignedCoo', header: 'CreateMoItem.CreateForm.AssignedCoo', sort: false, type: 'dropdown', optionsSource: 'countryOptions' },
    { field: 'assignedBrand', header: 'CreateMoItem.CreateForm.AssignedBrand', sort: false, type: 'dropdown', optionsSource: 'brandOptions' },
    { field: 'bbpLine', header: 'CreateMoItem.CreateForm.Line', sort: false, type: 'bbp', rules: this.bbpRules() },
    { field: 'bbpFace', header: 'CreateMoItem.CreateForm.Face', sort: false, type: 'bbp', rules: this.bbpRules() },
    { field: 'bbpMachine', header: 'CreateMoItem.CreateForm.Machine', sort: false, type: 'bbp', rules: this.bbpRules() },
    { field: 'bbpZone', header: 'CreateMoItem.CreateForm.Zone', sort: false, type: 'bbp', rules: this.bbpRules() },
    { field: 'bbpLot', header: 'CreateMoItem.CreateForm.Lot', sort: false, type: 'bbp', rules: this.bbpRules() },
    { field: 'errorReason', header: 'CreateMoItem.CreateForm.ErrorReason', sort: false, type: 'readonly' },
  ];

  preferenceKey: string = UserPreferenceConstants.CreateMoItemTableData;

  @ViewChild('createMoItemTable') createMoItemTable!: DataTableComponent;
  @ViewChild('etd') etd!: CalendarComponent;
  @ViewChild('eta') eta!: CalendarComponent;

  readonly editPermissionCode = PermissionConstants.ViewCreateDo;
  currentLang: string = this.laasSetting.language;
  createDoForm: FormGroup;
  createDoRequest: CreateDoByBBPRequest = new CreateDoByBBPRequest();
  createMoItemRequest: any[] = [];

  deliveryTypeOptions: LaasOption[] = [];
  urgentOptions: LaasOption[] = [];
  consigneeKeyOptions: any[] = [];
  consigneeList: any[] = [];
  plantList: any[] = [];
  plantMappingOptions: any[] = [];
  facilityOptions: any[] = [];
  specLocTypeOptions: any[] = [];
  isBondedOptions: any[] = [];
  createMoItemColumns: any[] = [];
  calloffReasonOptions: LaasOption[] = [];
  countryOptions: LaasOption[] = [];
  brandOptions: LaasOption[] = [];
  successfulDoNo: any[] = [];
  validFailSku: any[] = [];

  submitted = false;
  isCreateLoading = false;
  isFormOpen = true;

  displaySettingsDialog = false;
  isSkuUppercase = true;
  isCheckStock = true;
  isFileSelected = false;
  selectedFileName = '';

  displayImportDialog = false;
  isImportDialogLoading = false;
  doBBPFile: any;

  // successfulDoNo = '';
  isShowCreateDOResult = false;
  priority = false;
  ctn = false;
  urgentReason = '';
  errorReason = '';
  today: Date = startOfDay(new Date());

  constructor(injector: Injector,
              private formBuilder: FormBuilder,
              private doService: DeliveryOrderService,
              private commonDataService: CommonDataService,
              private poServiceService: PoServiceService,
              private plantService: PlantService,
              private dropdownOptionsService: DropdownOptionsService) {

      super(injector);

      this.createDoForm = this.formBuilder.group({
        plant: [null, Validators.required], // y
        deliveryType: [null, Validators.required], // y
        consigneeKey: [null, Validators.required], // y
        specLocType: [null, Validators.required], // y
        notes: [null, Validators.required],
        priority: null,
        ctn: null,
        urgentType: null,
        urgentReason: null,
        asnNo: [ null , [ Validators.pattern('^[0-9a-zA-Z-]+$'), Validators.maxLength(12) ] ],
        etd: null,
        eta: null,
        calloffReason: [null, Validators.required],
      });

      this.createMoItemColumns = [...this.columnSchemas];
  }

  async ngOnInit(): Promise<void> {

    this.poServiceService.getMappingFacilityOptions().subscribe({
      next: (result) => {
        this.plantMappingOptions = result.map((x: { optionDesc: any; optionCode: any; }) => ({
          label: x.optionCode,
          value: x.optionCode
        }));
        this.facilityOptions = result.map((x: { optionDesc: any; optionCode: any; }) => ({
          label: x.optionCode,
          value: x.optionDesc
        }));
      }
    });

    this.doService.getCustomerByTenant(['WISTRON']).subscribe({
      next: (result) => {
        this.consigneeList = result;
      }
    });

    this.plantService.getPlantOptions().subscribe({
      next: (result)=>{
        this.plantList = result;
      }
    });

    this.doService.getSpecLocType().subscribe({
      next:(result) =>{
        const seenBondedOptions = new Set<string>();
        this.isBondedOptions = result.specLocTypeList
          .filter((x: { specLocType: any; glAttribute01: any; }) => {
            const key = `${x.specLocType}__${x.glAttribute01}`;
            if (seenBondedOptions.has(key)) return false;
            seenBondedOptions.add(key);
            return true;
          })
          .map((x: { specLocType: any; glAttribute01: any; }) => ({
            label: x.specLocType,
            value: x.glAttribute01
          }));
      }
    });

    this.deliveryTypeOptions = [
      { label: 'LOCAL', value: 'LOCAL' },
      { label: 'EXPORT', value: 'EXPORT' }
    ];

    this.urgentOptions = [
      { label: '急單 8HR', value: 'PR1-8' },
      { label: '急單 4HR', value: 'PR1-4' },
    ];

    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'CalloffReason').subscribe({
      next: result => {
        this.calloffReasonOptions = [...result].map((x: any) => {
          return {
            label: `${x.codeDesc} - ${x.remark}`,
            value: x.codeKey,
          };
        });
      }
    });

    this.commonDataService.getCountry().subscribe({
      next: (response) => {
        for (const country of response) {
          if (this.laasSetting.language == GlobalConstants.defaultLanguage) {
            this.countryOptions.push({
              label: `${country.countryCodeISO} - ${country.countryName}`,
              value: country.countryCodeISO
            })
          }
          else {
            this.countryOptions.push({
              label: `${country.countryCodeISO} - ${country.countryEnName}`,
              value: country.countryCodeISO
            })
          }
        }
      }
    })

    this.dropdownOptionsService.getBrandOptions(1)
      .subscribe({
        next: (options: { brandName: string }[]) => {
          this.brandOptions = options.map(x => ({ label: x.brandName, value: x.brandName }));
        }
    });

    this.createDoRequest.deliveryType = 'LOCAL';
    this.createDoForm.controls.deliveryType.setValue('LOCAL');

    this.createDoRequest.storer = 'WISTRON_BBP';
  }

  createDo(): void {
    this.isCreateLoading = true;
    console.log('createDoForm',this.createDoForm)
    if (this.createDoForm.invalid || !this.checkMoItemValid()) {
      this.submitted = true;
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFieldsOrFieldsFormat'));
      this.isCreateLoading = false;
      return;
    }

    if (this.createDoRequest.consigneeKey === this.createDoRequest.specLocType) {
      this.toastService.error(this.translateService.instant('CreateDo.Msg.ConsigneeKeyAndSpecLocTypeCantBeSame'));
      this.isCreateLoading = false;
      return;
    }

    if (this.createDoForm?.controls?.asnNo?.value && this.createDoForm?.controls?.asnNo?.value.length > 12) {
      this.toastService.warn(this.translateService.instant('CreateDo.Msg.AsnNoOverMaxLength'));
      this.isCreateLoading = false;
      return;
    }

    if (this.createDoForm?.controls?.etd?.value && this.createDoForm?.controls?.eta?.value &&
      this.createDoForm?.controls?.etd?.value > this.createDoForm?.controls?.eta?.value) {
      this.toastService.warn(this.translateService.instant('CreateDo.Msg.EtaCannotBeBeforeEtd'));
      this.isCreateLoading = false;
      return;
    }

    if (this.createMoItemRequest.length === 0) {
      this.toastService.warn(this.translateService.instant('CreateDo.Msg.NoMoItem'));
      this.isCreateLoading = false;
      return;
    }

    // 叫料原因為 IQCXray 但客戶代碼不包含 IQC 時，警告並阻擋送出
    if (this.createDoRequest.calloffReason === 'IQCXray') {
      const selectedConsignee = this.consigneeList.find(x => x.customerCode == this.createDoRequest.consigneeKey);
      const consigneeLabel = (selectedConsignee?.customerCode || '') + (selectedConsignee?.customerName || '');
      if (!consigneeLabel.toUpperCase().includes('IQC')) {
        this.toastService.warn(this.translateService.instant('CreateDo.Msg.IQCXrayRequiresIQCCustomer'));
        this.isCreateLoading = false;
        return;
      }
    }

    this.createDoRequest.moItemList = this.buildSanitizedMoItemsForRequest();
    this.createDoRequest.priority = this.priority ? '1' : '5';
    this.createDoRequest.ctn = this.ctn ? 'CTN-1' : null;
    this.createDoRequest.checkInventoryFlag = this.isCheckStock;
    this.createDoRequest.asnNo = this.createDoForm.controls.asnNo.value;
    this.createDoRequest.etd = this.createDoForm.controls.etd.value;
    this.createDoRequest.eta = this.createDoForm.controls.eta.value;

    for (const moItem of this.createMoItemRequest) {
      const bbpFields = [moItem.bbpLine,moItem.bbpFace,moItem.bbpMachine,moItem.bbpZone];
  
      if (bbpFields.some(x => x && x.length > 50)) {
        this.toastService.warn(this.translateService.instant('Common.Message.CheckMaximumWord', { field: 'Line/Face/Machine/Zone', max: 50 }));
        this.isCreateLoading = false;
        return;
      }

      if (moItem.bbpLot) {
        if (!/^\d+(\.\d+)?$/.test(moItem.bbpLot)) {
          this.toastService.warn(this.translateService.instant('Common.Message.AllowedCharacterOnlyNumber',{ field: 'Lot'}));
          this.isCreateLoading = false;
          return;
        }
        if (moItem.bbpLot.length > 15) {
          this.toastService.warn(this.translateService.instant('Common.Message.CheckMaximumWord',{ field: 'Lot', max: 15 }));
          this.isCreateLoading = false;
          return;
        }
      }
    }

    console.log(this.createDoRequest);

    //  打DO
    this.doService.createDoByBBP(this.createDoRequest).subscribe({
      next: (response: {
        doNo: string[];
        flag: string;
        message: string;
        failSku: string[];
        inventroyCheckResult?: {
          sku: string;
          qtyAvailable: string;
          qtyTotalNeed: string;
        }[];
      }) => {
        console.log(response);
        if (response.flag === 'F') {
          console.log(response.doNo);
          this.successfulDoNo = response.doNo;
          this.isShowCreateDOResult = true;
          this.priority = false;
          this.createDoForm.reset();
          this.createDoForm.controls.urgentReason.clearValidators();
          this.createDoForm.controls.urgentReason.updateValueAndValidity();
          this.createMoItemRequest = [];
          this.createMoItemTable.value = this.createMoItemRequest;
          this.createMoItemTable.initSetupValue();
          this.validFailSku = [];
          this.createDoRequest.facility = null;
          this.createDoRequest.consigneeCompany = null;
          this.createDoRequest.consigneeAddress1 = null;
          this.createDoRequest.consigneeAddress2 = null;
          this.createDoRequest.asnNo = null;
          this.createDoRequest.etd = null;
          this.createDoRequest.eta = null;
          this.etd.ngOnInit();
          this.eta.ngOnInit();
        }

        if (response.flag === 'E') {
          this.successfulDoNo = response.doNo;
          this.isShowCreateDOResult = true;
          if ((response.inventroyCheckResult && response.inventroyCheckResult.length > 0)) {
            this.updateMoItemErrors(response.inventroyCheckResult, response.failSku);
            this.toastService.warn(this.translateService.instant('CreateDo.Warn.CheckInventoryFailed'));
          }
        }
      },
      error: (err: any) => {
        console.log(err);
        console.log(err.error);
        this.toastErrorMessage(err.error);
        this.isCreateLoading = false;
      },
      complete: () => {
        this.submitted = false;
        this.isCreateLoading = false;
      }
    });
  }

  updateMoItemErrors(
    inventroyCheckResult: { sku: string; qtyAvailable: string; qtyTotalNeed: string }[],
    failSku: string[]
  ): void {

    // 只取出失敗的Sku
    this.validFailSku = failSku ?? [];
    this.createMoItemRequest = this.createMoItemRequest.filter(moItem => this.validFailSku.includes(moItem.sku));


    this.createMoItemRequest.forEach(moItem => {
      const inventoryItem = inventroyCheckResult.find(item => item.sku === moItem.sku);
      console.log('inventoryItem', inventoryItem);
      if (inventoryItem) {
        moItem.errorReason = 'Total qty Needed :' + inventoryItem.qtyTotalNeed + ' / Total qty Available :' + inventoryItem.qtyAvailable;
      }
    });

  }

  dataInputValid = (rowData: any): boolean => {
    let result = true;
    console.log(rowData);

    if (rowData) {
      const isIQCXray = this.createDoRequest.calloffReason === 'IQCXray';
      if ( isNullOrEmpty(rowData.sku?.trim()) || isNullOrEmpty(rowData.originalQty)) {
        this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
        result = false;
      }
      if (isIQCXray && isNullOrEmpty(rowData.inspectionLot)) {
        this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
        result = false;
      }
    }
    return result;
  }

  getCellViewModel(rowData: any, col: { field: string }): CellViewModel {
    const schema = this.columnSchemas.find(x => x.field === col.field);
    const context = this.getRuleContext();
    const visible = schema?.rules?.visible ? schema.rules.visible(context) : true;
    const editable = visible && (schema?.rules?.editable ? schema.rules.editable(context) : true);
    return {
      editable,
      visible,
      value: visible ? rowData?.[col.field] : null
    };
  }

  getColumnType(col: { field: string }): CreateDoColumnSchema['type'] {
    return this.getColumnSchema(col)?.type ?? 'readonly';
  }

  isColumnRequired(col: { field: string }): boolean {
    return !!this.getColumnSchema(col)?.required;
  }

  isCellRequired(col: { field: string }, rowData: any): boolean {
    if (rowData?.[col.field]) {
      return false;
    }
    if (this.isColumnRequired(col)) {
      return true;
    }
    return col.field === 'inspectionLot' && this.createDoRequest.calloffReason === 'IQCXray';
  }

  showFieldConstraint(col: { field: string }): boolean {
    return !!this.getColumnSchema(col)?.showFieldAConstraint;
  }

  getDropdownOptions(col: { field: string }): LaasOption[] {
    const optionsSource = this.getColumnSchema(col)?.optionsSource;
    if (optionsSource === 'countryOptions') {
      return this.countryOptions;
    }
    if (optionsSource === 'brandOptions') {
      return this.brandOptions;
    }
    return [];
  }

  onCalloffReasonChange(event: any): void {
    const previousCalloffReason = this.createDoRequest.calloffReason;
    this.createDoRequest.calloffReason = event?.value;
    if (event?.value === 'IQCXray') {
      this.ctn = true;
      this.createDoForm.controls.ctn.setValue(true);
      this.isCheckStock = false;
      this.createMoItemRequest.forEach((moItem) => {
        moItem.originalQty = 1;
      });
    } else if (previousCalloffReason === 'IQCXray') {
      // 由 IQCXray 切回其他叫料原因時要還原庫存檢查，否則會沿用 IQC 專用的不檢查庫存設定
      this.isCheckStock = true;
    }
  }

  private buildSanitizedMoItemsForRequest(): any[] {
    return this.createMoItemRequest.map((moItem) => {
      const sanitized = { ...moItem };
      this.columnSchemas.forEach((schema) => {
        if (!schema.rules?.sanitizeWhenHidden) {
          return;
        }
        const isVisible = schema.rules.visible?.(this.getRuleContext()) ?? true;
        if (!isVisible) {
          sanitized[schema.field] = null;
        }
      });
      return sanitized;
    });
  }

  private bbpRules(): NonNullable<CreateDoColumnSchema['rules']> {
    return {
      editable: (context) => context.calloffReason === this.MO_USE_REASON,
      visible: (context) => context.calloffReason === this.MO_USE_REASON,
      sanitizeWhenHidden: true
    };
  }

  private getRuleContext(): CreateDoRuleContext {
    return {
      calloffReason: this.createDoForm.controls.calloffReason.value
    };
  }

  private getColumnSchema(col: { field: string }): CreateDoColumnSchema | undefined {
    return this.columnSchemas.find(x => x.field === col.field);
  }

  checkMoItemValid(): boolean {
    let result = true;
    const isIQCXray = this.createDoRequest.calloffReason === 'IQCXray';
    this.createMoItemRequest.forEach((moItem) => {
      if (isNullOrEmpty(moItem.sku?.trim()) || isNullOrEmpty(moItem.originalQty)) {
        result = false;
        return;
      }
      if (isIQCXray && isNullOrEmpty(moItem.inspectionLot)) {
        result = false;
      }
    });
    return result;
  }

  createNewRow = (rowId: number): any => {
    const isIQCXray = this.createDoRequest.calloffReason === 'IQCXray';
    return {
      sku: null,
      originalQty: isIQCXray ? 1 : null,
      errorReason: null,
      assignedVendorLot: null,
      assignedVendorDC: null,
      assignedCoo: null,
      assignedBrand: null,
      inspectionLot: null,
      bbpLine: null,
      bbpFace: null,
      bbpMachine: null,
      bbpZone: null,
      bbpLot: null,
      rowId
    };
  }

  onPlantChange(event: any): void {
    this.resetConsignee()
    const plantId = event.value;
    const selectedPlant = this.facilityOptions.find(option => option.label === plantId);
    this.createDoRequest.facility = selectedPlant?.value ?? null;

    const customerCodeSet = new Set(this.plantList.filter(x => x.plantId == plantId).map(x => x.customerCode))
    this.consigneeKeyOptions = this.consigneeList
      .filter(x => customerCodeSet.has(x.customerCode))
      .sort((a, b) => this.compareConsigneePrefix(a.customerCode, b.customerCode, plantId))
      .map(y => ({
        label: y.customerCode + ' - ' + y.customerName,
        value: y.customerCode
      }))
  }

  /** 客戶代碼的廠區前綴,取第一個底線之前的片段;無底線時為整串。 */
  private consigneePrefix(customerCode: string): string {
    const code = customerCode ?? '';
    const underscore = code.indexOf('_');
    return underscore < 0 ? code : code.slice(0, underscore);
  }

  /**
   * 依廠區前綴比較客戶代碼:所選廠區自身前綴排最前,其餘前綴依字母序各自成組。
   * 同前綴內回傳 0,由穩定排序保留 API 已依 SortPriority 排好的結果,
   * 使優先序只有後端一份定義。廠區設定含三個以上前綴時,各組才不會互相交錯。
   * (The selected plant's own prefix leads; remaining prefixes group alphabetically.
   *  Returning 0 within one prefix lets the stable sort keep the API's SortPriority order,
   *  so groups stay contiguous once a plant maps more than two prefixes.)
   */
  private compareConsigneePrefix(codeA: string, codeB: string, plantId: string): number {
    const prefixA = this.consigneePrefix(codeA);
    const prefixB = this.consigneePrefix(codeB);
    if (prefixA === prefixB) {
      return 0;
    }
    const isOwnA = prefixA === plantId;
    const isOwnB = prefixB === plantId;
    if (isOwnA !== isOwnB) {
      return isOwnA ? -1 : 1;
    }
    return prefixA.localeCompare(prefixB);
  }

  resetConsignee(): void {
    this.consigneeKeyOptions = []
    this.createDoForm.controls['consigneeKey'].setValue(null)
    this.createDoRequest.consigneeCompany = null;
    this.createDoRequest.consigneeAddress1 = null;
    this.createDoRequest.consigneeAddress2 = null;
    this.specLocTypeOptions = []
    // 重置時恢復紙箱備料可編輯狀態
    this.createDoForm.controls.ctn.enable();
  }

  onConsigneeChange(event: any): void {
    const plant = this.createDoRequest.plant;
    if(plant == null) {
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFieldsOrFieldsFormat'));
      return
    }

    let selectedConsignee = this.consigneeList.find(x => x.customerCode == event.value)
    if (selectedConsignee == null) {
      this.toastService.warn(this.translateService.instant('CreateDo.Msg.ConsigneeKeyNotFound'));
      return
    }

    // 當客戶代碼或客戶名稱包含 "IQC" 時，自動勾選紙箱備料且不允許取消
    const consigneeLabel = (selectedConsignee?.customerCode || '') + (selectedConsignee?.customerName || '');
    if (consigneeLabel.toUpperCase().includes('IQC')) {
      this.ctn = true;
      this.createDoForm.controls.ctn.setValue(true);
      this.createDoForm.controls.ctn.disable();
      // 客戶代碼包含 IQC 時，叫料原因自動選為 IQCXray
      this.createDoRequest.calloffReason = 'IQCXray';
      this.createDoForm.controls.calloffReason.setValue('IQCXray');
      this.onCalloffReasonChange({ value: 'IQCXray' });
    } else {
      this.createDoForm.controls.ctn.enable();
    }

    this.createDoRequest.consigneeCompany = selectedConsignee.customerName;
    if (selectedConsignee && selectedConsignee.address) {
      const addressParts = selectedConsignee.address.split(',');
      this.createDoRequest.consigneeAddress1 = addressParts[0];
      this.createDoRequest.consigneeAddress2 = addressParts[1];
    } else {
      this.createDoRequest.consigneeAddress1 = selectedConsignee.customerName;
      this.createDoRequest.consigneeAddress2 = selectedConsignee.customerName;
    }

    const specLocTypeSet: Set<string> = new Set<string>();
    const plantDetailList = this.plantList.filter(x => x.plantId == plant && x.specLocType != 'ALL')

    const isAllSpecLocType = selectedConsignee.customerCode.includes('LK-WISTRON')
    for (const plantBean of plantDetailList) {
      if (isAllSpecLocType || plantBean.customerCode == selectedConsignee.customerCode) {
        specLocTypeSet.add(plantBean.specLocType)
      }
    }

    if (specLocTypeSet.size > 0) {
      this.specLocTypeOptions = Array.from(specLocTypeSet).map(specLocType => ({
        label: specLocType,
        value: specLocType
      }));
    }
  }

  onSpecLocTypeChange(event: any): void {
    const selectedSpecLocType = this.isBondedOptions.find(option => option.label === event.value);
    if (selectedSpecLocType) {
      this.createDoRequest.isBonded = selectedSpecLocType.value;
    }
  }

  showSettingsDialog(): void {
    this.displaySettingsDialog = true;
  }


  onSkuInput(event: any, rowData: any): void {
    if (this.isSkuUppercase) {
      const inputValue = event.target.value;
      rowData.sku = inputValue.toUpperCase();
    }
  }

  showImportDialog(): void {
    this.displayImportDialog = true;
  }

  deleteRow(rowData: any): void {
    console.log(this.createMoItemRequest);
    this.createMoItemTable.deleteRow(rowData);
    console.log(this.createMoItemRequest);
  }

  onFileSelected(event: any): void {
    this.doBBPFile = event.target.files[0];
    if (this.doBBPFile) {
      this.isFileSelected = true;
      this.selectedFileName = this.doBBPFile.name;
      event.target.value = '';
    }
  }


  uploadFile(): void {
    this.isImportDialogLoading = true;
    this.doService.transBBPDoFile(this.doBBPFile, this.isCheckStock, this.isSkuUppercase).subscribe({
      next:  (resp: {
        facility: string;
        storer: string;
        deliveryType: string;
        notes: string;
        consigneeKey: string;
        consigneeCompany: string;
        consigneeAddress1: string;
        consigneeAddress2: string;
        specLocType: string;
        plant: string;
        priority: string;
        isBonded: string;
        etd: Date;
        eta: Date;
        checkInventoryFlag: boolean;
        urgentType: string;
        urgentReason: string;
        ctn: string;
        asnNo: string;
        calloffReason: string;
        moItemList?: {
          sku: string;
          originalQty: number;
          inspectionLot: string;
        }[];
      }) => {
        console.log(resp);
        this.createDoRequest.plant = resp.plant;
        this.createDoForm.controls['plant'].setValue(resp.plant);
        this.onPlantChange({ value: this.createDoRequest.plant });
        this.createDoRequest.facility = resp.facility;
        this.createDoRequest.storer = resp.storer;
        this.createDoRequest.deliveryType = resp.deliveryType;
        this.createDoRequest.notes = resp.notes;
        this.createDoRequest.consigneeKey = resp.consigneeKey;
        this.onConsigneeChange({ value : this.createDoRequest.consigneeKey });
        this.createDoRequest.consigneeCompany = resp.consigneeCompany;
        this.createDoRequest.consigneeAddress1 = resp.consigneeAddress1;
        this.createDoRequest.consigneeAddress2 = resp.consigneeAddress2;
        this.createDoRequest.specLocType = resp.specLocType;
        this.priority = resp.priority === '1';
        this.createDoRequest.isBonded = resp.isBonded;
        if (resp.etd) {
          this.createDoForm.controls.etd.setValue(new Date(resp.etd));
          (this.etd as CalendarComponent).date = new Date(resp.etd);
        }
        if (resp.eta) {
          this.createDoForm.controls.eta.setValue(new Date(resp.eta));
          (this.eta as CalendarComponent).date = new Date(resp.eta);
        }
        this.createDoRequest.checkInventoryFlag = resp.checkInventoryFlag;
        this.createDoRequest.urgentType = resp.urgentType;
        this.createDoRequest.urgentReason = resp.urgentReason;
        this.ctn = resp.ctn === 'CTN-1';
        this.createDoForm.controls['asnNo'].setValue(resp.asnNo);
        // 欄位提示以 touched 為顯示條件，Excel 帶入的值須標記後才會顯示長度與格式錯誤
        this.createDoForm.controls['asnNo'].markAsTouched();
        this.createDoForm.controls['calloffReason'].setValue(resp.calloffReason);
        this.createMoItemRequest = resp.moItemList ?? [];
        this.putRowId(this.createMoItemRequest);
        // 無條件呼叫，讓 isCheckStock 一定會依 Excel 最終的叫料原因重新計算，
        // 避免 onConsigneeChange 若判定為 IQC 客戶而暫時關閉庫存檢查後，因 Excel 叫料原因非 IQCXray 而沒有機會還原
        this.onCalloffReasonChange({ value: resp.calloffReason });
        if (resp.calloffReason === 'IQCXray') {
          this.createDoForm.controls.ctn.disable();
        }
      },
      error: rsp => {
        console.log('error', rsp);
        this.doBBPFile = undefined;
        this.isFileSelected = false;
        this.isImportDialogLoading = false;
        const errorMsg = rsp?.error?.errorMsg;
        if (!isNullOrEmpty(errorMsg) && errorMsg.includes('[Parse Error]')) {
          const parseErrorDetail = errorMsg.replace('[Parse Error]', '').trim();
          this.toastService.error(this.translateService.instant('CreateDo.Msg.CheckExcelRequiredFields') + '\r\n' + 'Excel ' + parseErrorDetail);
          return;
        }
        if (!isNullOrEmpty(errorMsg)) {
          this.toastService.error(errorMsg);
          return;
        }
        this.toastService.error(this.translateService.instant('ReceiptFormQuery.Error.UploadFail'));
      },
      complete: () => {
        this.displayImportDialog = false;
        this.doBBPFile = undefined;
        this.isFileSelected = false;
        this.isImportDialogLoading = false;
        console.log('this.createDoRequest',this.createDoRequest)
      }
    });
  }

  downloadTemplateBtn(): void {
    const fileType = 'CreateBBPDoTemplate';

    this.commonDataService.getSystemParam(fileType).subscribe({
      next: result => {
        const searchObj = {
          sn: result[0].sn,
          version: result[0].version
        };

        this.doService.downloadFile(searchObj).subscribe(x => {
          const blob = new Blob([x], { type: 'application/octet-stream' });
          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = 'CreateBBPDoTemplate.xlsx';
          link.click();
        });
      }
    });
  }

  onPriorityChange(event: any): void {
    if (event.checked) {
      this.createDoRequest.urgentType = 'PR1-8';
      this.createDoForm.controls.urgentReason.setValidators(Validators.required);
    } else {
      this.createDoForm.controls.urgentReason.clearValidators();
    }
    this.createDoForm.controls.urgentReason.updateValueAndValidity();
  }

  dateOnChange(date: any, type: string): void {
    switch (type) {
      case 'etd':
        this.createDoForm.controls.etd.setValue(date.date);
        break;
      case 'eta':
        this.createDoForm.controls.eta.setValue(date.date);
        break;
    }
  }

  putRowId(array: any[]) {
    let rowId = 1;
    for (const val of array) {
      val.rowId = rowId;
      rowId = rowId + 1;
    }
  }

}
